import { useEffect, useState, type ReactNode } from 'react'
import { LENGTHS, pageButtons } from '../../ui/DataTable'

/**
 * Cautarea, numarul de randuri si paginarea celor trei liste din
 * Settings → Softsee (Manage applications, Software Tagging, Software
 * Licenses), cu markupul lui DataTables 1.10 - `dom`-ul pe care il foloseste
 * zona peste tot:
 *
 *   <"row"<"col-sm-6 dataTableF"f>>
 *   <"row"<"col-sm-12"rt>>
 *   <"row"<"col-sm-2"l><"col-sm-4 aright"i><"col-sm-6"p>>
 *
 * NU e `ui/DataTable`. Acela isi trimite cautarea in ANTETUL PAGINII si
 * paginarea in SUBSOLUL ei (`tk-header-tools`, `tk-footer-list`), prin
 * portaluri - locuri bune pentru o lista care tine tot ecranul (Customers,
 * Inventoree), dar nu pentru un panou care sta in `col-md-9`, langa coloana lui
 * de alegere si sub capul lui de zona: uneltele ar pleca din panou in cadrul
 * paginii. Aceleasi motive pentru care `settings/company_reports/ReportTable`
 * isi deseneaza singur controalele, si acelasi markup ca el.
 *
 * Sagetile inainte/inapoi sunt Font Awesome (`fas fa-chevron-left`), ca in
 * `ui/DataTable`, NU Glyphicons: fontul lui Bootstrap 3 nu ajunge sub `/app`.
 * Cererea pentru `fonts/vendor/bootstrap/dist/glyphicons-halflings-regular.woff2`
 * intoarce `index.html`-ul aplicatiei (3.743 de octeti, `text/html`), deci
 * `@font-face` pica si in locul chevronului se deseneaza casuta de glif lipsa:
 * masurat in Edge pe foile de pe server, U+E079 da exact aceiasi 50 de pixeli
 * ca un punct de cod pe care nu-l are niciun font, pixel cu pixel. Fontul Font
 * Awesome vine de acolo intreg (78 KB, `font/woff2`) si il foloseste toata
 * consola - sageata lui are 55 de pixeli si e un chevron adevarat.
 *
 * Tabelul ramane AL PANOULUI - se da ca `children`, neatins. Aici sta doar ce
 * e in jurul lui.
 */
export function SoftseeListChrome({
  id,
  search,
  onSearch,
  start,
  length,
  total,
  fetching,
  onStart,
  onLength,
  children,
}: {
  /** `id`-ul tabelului; controalele poarta `<id>_filter`, `<id>_length`, `<id>_paginate`, ca in DataTables. */
  id: string
  search: string
  onSearch(next: string): void
  start: number
  length: number
  total: number
  fetching: boolean
  onStart(next: number): void
  onLength(next: number): void
  children: ReactNode
}) {
  const [draft, setDraft] = useState(search)

  /* Cautarea pleaca la 400 ms dupa ultima tasta, ca in DataTables. */
  useEffect(() => {
    if (draft === search) return
    const t = window.setTimeout(() => onSearch(draft), 400)

    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  /* Coloana de alegere schimbata din afara goleste casuta. */
  useEffect(() => {
    setDraft(search)
  }, [search])

  const pages = Math.max(1, Math.ceil(total / length))
  const page = Math.min(Math.floor(start / length), pages - 1)
  const goTo = (p: number) => onStart(Math.max(0, Math.min(pages - 1, p)) * length)

  return (
    <div id={`${id}_wrapper`} className="dataTables_wrapper form-inline dt-bootstrap no-footer">
      <div className="row">
        <div className="col-sm-6 dataTableF">
          <div id={`${id}_filter`} className="dataTables_filter input-icon">
            <label>
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                className="form-control input-sm"
                placeholder="Search..."
                aria-controls={id}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </label>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${id}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          {children}
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${id}_length`}>
            <label>
              <select
                name={`${id}_length`}
                aria-controls={id}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${id}_info`} role="status" aria-live="polite">
            {total === 0
              ? 'Showing 0 to 0 of 0 entries'
              : `Showing ${start + 1} to ${Math.min(start + length, total)} of ${total} entries`}
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${id}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page === 0 ? ' disabled' : ''}`} id={`${id}_previous`}>
                <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => (e.preventDefault(), goTo(page - 1))}>
                  <i className="fas fa-chevron-left" aria-hidden="true"></i>
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li className="paginate_button disabled" key={`e${i}`}>
                    <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li className={`paginate_button${b === page ? ' active' : ''}`} key={b}>
                    <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => (e.preventDefault(), goTo(b))}>
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${id}_next`}>
                <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => (e.preventDefault(), goTo(page + 1))}>
                  <i className="fas fa-chevron-right" aria-hidden="true"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
